import { TemplateOperationExtrasProvider, type TemplateOperationCompleteEvent } from 'adflow';
import { useCallback, useMemo, type FC, type PropsWithChildren } from 'react';

import { redeemTemplateSavedVersion } from '../savedVersions/fetchTemplateSavedVersions';
import { postTemplateOperationAudit } from './postTemplateOperationAudit';

type Props = PropsWithChildren<{
  templateId?: string;
}>;

const TemplateBuilderOperationAuditProvider: FC<Props> = ({ templateId, children }) => {
  const onOperationComplete = useCallback(
    (event: TemplateOperationCompleteEvent) => {
      const resolvedTemplateId = templateId ?? event.templateId;
      if (!resolvedTemplateId) {
        return;
      }
      void postTemplateOperationAudit(resolvedTemplateId, event).catch((error) => {
        console.warn('[Template operation audit] ingest failed', error);
      });
    },
    [templateId],
  );

  const redeemSavedVersionRestore = useCallback(
    async (args: {
      restoreToken: string;
      expectedWorkingRevision: number;
      expectedPersistedRevision: number;
    }) => {
      if (!templateId) {
        throw new Error('Template id is not available.');
      }
      return redeemTemplateSavedVersion(templateId, args);
    },
    [templateId],
  );

  const value = useMemo(
    () => ({
      templateId,
      onOperationComplete,
      redeemSavedVersionRestore: templateId ? redeemSavedVersionRestore : undefined,
    }),
    [onOperationComplete, redeemSavedVersionRestore, templateId],
  );

  return (
    <TemplateOperationExtrasProvider value={value}>{children}</TemplateOperationExtrasProvider>
  );
};

export default TemplateBuilderOperationAuditProvider;
